import React, { useEffect } from 'react';
import { useWizard } from '../../contexts/WizardContext';
import { submitOnboarding } from '../../services/trustline';
import { getCurrentUserToken } from '../../config/firebase';
import { isStellarNetwork } from '../../config/networks';
import './Step7Submission.css';

const Step7Submission: React.FC = () => {
  const { wizardData, updateWizardData } = useWizard();
  const stellar = isStellarNetwork(wizardData.chain);

  useEffect(() => {
    // Only submit if not already submitted and not in error state
    if (!wizardData.submissionSuccess && !wizardData.submissionError && !wizardData.isSubmitting) {
      const submit = async () => {
        if (!wizardData.chain) {
          updateWizardData({
            isSubmitting: false,
            submissionError: 'Chain information is missing',
          });
          return;
        }

        if (!wizardData.email) {
          updateWizardData({
            isSubmitting: false,
            submissionError: 'Email is missing. Please complete the authentication steps.',
          });
          return;
        }

        // Get a fresh token from Firebase (auto-refreshes if expired)
        const firebaseToken = await getCurrentUserToken();
        if (!firebaseToken) {
          updateWizardData({
            isSubmitting: false,
            submissionError: 'Authentication token is missing. Please complete the authentication steps.',
          });
          return;
        }

        updateWizardData({ isSubmitting: true, submissionError: null });

        try {
          const result = await submitOnboarding(
            wizardData,
            wizardData.chain,
            wizardData.email,
            firebaseToken
          );

          if (result.success) {
            updateWizardData({
              isSubmitting: false,
              submissionSuccess: true,
              submittedChainId: wizardData.chain.chainId.toString(),
              submittedContractAddress: isStellarNetwork(wizardData.chain)
                ? wizardData.contractAddress
                : wizardData.contractAddress,
            });
          } else {
            updateWizardData({
              isSubmitting: false,
              submissionError: result.error || 'Submission failed',
            });
          }
        } catch (error: any) {
          updateWizardData({
            isSubmitting: false,
            submissionError: error.message || 'An error occurred during submission',
          });
        }
      };

      submit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRetry = async () => {
    if (!wizardData.chain) {
      updateWizardData({
        isSubmitting: false,
        submissionError: 'Chain information is missing',
      });
      return;
    }

    if (!wizardData.email) {
      updateWizardData({
        isSubmitting: false,
        submissionError: 'Email is missing. Please complete the authentication steps.',
      });
      return;
    }

    // Get a fresh token from Firebase (auto-refreshes if expired)
    const firebaseToken = await getCurrentUserToken();
    if (!firebaseToken) {
      updateWizardData({
        isSubmitting: false,
        submissionError: 'Authentication token is missing. Please complete the authentication steps.',
      });
      return;
    }

    updateWizardData({ isSubmitting: true, submissionError: null });

    try {
      const result = await submitOnboarding(
        wizardData,
        wizardData.chain,
        wizardData.email,
        firebaseToken
      );
      if (result.success) {
        updateWizardData({
          isSubmitting: false,
          submissionSuccess: true,
          submittedChainId: wizardData.chain.chainId.toString(),
          submittedContractAddress: wizardData.contractAddress,
        });
      } else {
        updateWizardData({
          isSubmitting: false,
          submissionError: result.error || 'Submission failed',
        });
      }
    } catch (error: any) {
      updateWizardData({
        isSubmitting: false,
        submissionError: error.message || 'An error occurred during submission',
      });
    }
  };

  if (wizardData.submissionSuccess) {
    // Count methods that are actually mapped (have a policy assigned)
    const mappedMethodsCount = wizardData.methodMappings.filter(m => m.policyId !== null).length;

    // Success state - show summary
    return (
      <div className="wizard-step step7-submission step7-success-state">
        <div className="step7-success-content">
          <div className="step7-success-icon">✓</div>
          <h2 className="step7-title">Your dApp is now protected by Trustline!</h2>
          <p className="step7-message">
            Your Trustline configuration has been successfully registered and activated.
          </p>

          <div className="step7-info-card">
            <h3 className="step7-info-title">Configuration Summary</h3>
            <div className="step7-info-item">
              <span className="step7-info-label">dApp:</span>
              <span className="step7-info-value">{wizardData.dAppName}</span>
            </div>
            {wizardData.email && (
              <div className="step7-info-item">
                <span className="step7-info-label">Trustline Account:</span>
                <span className="step7-info-value">{wizardData.email}</span>
              </div>
            )}
            <div className="step7-info-item">
              <span className="step7-info-label">Chain:</span>
              <span className="step7-info-value">{wizardData.chain?.name}</span>
            </div>
            <div className="step7-info-item">
              <span className="step7-info-label">Contract:</span>
              <span className="step7-info-value step7-address">{wizardData.contractAddress}</span>
            </div>
            <div className="step7-info-item">
              <span className="step7-info-label">
                {stellar ? 'Validation Engine:' : 'Validation Engine Proxy:'}
              </span>
              <span className="step7-info-value step7-address">{wizardData.validationEngineProxyAddress}</span>
            </div>
            {!stellar && (
              <div className="step7-info-item">
                <span className="step7-info-label">Validation Engine Implementation:</span>
                <span className="step7-info-value step7-address">{wizardData.validationEngineImplementationAddress}</span>
              </div>
            )}
            <div className="step7-info-item">
              <span className="step7-info-label">Policies:</span>
              <span className="step7-info-value">{wizardData.namedPolicies.length}</span>
            </div>
            <div className="step7-info-item">
              <span className="step7-info-label">Methods Mapped to Policies:</span>
              <span className="step7-info-value">{mappedMethodsCount}</span>
            </div>
            <div className="step7-info-item">
              <span className="step7-info-label">Trustline validation:</span>
              <span className="step7-info-value">{mappedMethodsCount > 0 ? 'Enabled' : 'Disabled'}</span>
            </div>
            <div className="step7-info-item">
              <span className="step7-info-label">Sanctions list checks:</span>
              <span className="step7-info-value">{wizardData.sanctionsEnabled === true ? 'Enabled' : wizardData.sanctionsEnabled === false ? 'Disabled' : '—'}</span>
            </div>
            {wizardData.sanctionsEnabled === true && wizardData.sanctionsOracleKey && (
              <div className="step7-info-item">
                <span className="step7-info-label">Sanctions oracle key:</span>
                <span className="step7-info-value">{wizardData.sanctionsOracleKey}</span>
              </div>
            )}
          </div>

          <p className="step7-message">
            Redirecting to your dApp dashboard…
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="wizard-step step7-submission">
      <h2 className="wizard-step-title">Submitting Configuration</h2>
      <p className="wizard-step-description">
        Please wait while we register your Trustline configuration...
      </p>

      {wizardData.isSubmitting ? (
        <div className="step7-loading">
          <div className="step7-spinner"></div>
          <p>Submitting your configuration...</p>
        </div>
      ) : wizardData.submissionError ? (
        <div className="step7-error-container">
          <div className="wizard-error">
            <h3>Submission Failed</h3>
            <p>{wizardData.submissionError}</p>
          </div>
          <button
            onClick={handleRetry}
            className="step7-retry-btn"
          >
            Retry
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default Step7Submission;

